import { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { Bell, Clock, CalendarClock, BookOpen, Sparkles, DollarSign, Mail, Quote, Lightbulb, RefreshCcw, TrendingUp } from 'lucide-react';

const ICONS = {
  clock: Clock, calendar: CalendarClock, book: BookOpen, sparkles: Sparkles,
  dollar: DollarSign, mail: Mail, quote: Quote, lightbulb: Lightbulb,
};

const SEVERITY_STYLES = {
  high:   { tint: '#DC2626', bg: '#FEF2F2', label: 'Needs attention' },
  medium: { tint: '#E8C265', bg: '#FFFBEB', label: 'Worth doing soon' },
  info:   { tint: '#5B7C99', bg: '#F0F5FA', label: 'Heads up' },
};

export default function AlertsPanel({ onAction, onOpenArticle }) {
  const [alerts, setAlerts] = useState([]);
  const [momentum, setMomentum] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [alertsRes, momentumRes] = await Promise.all([
        api.get('/admin/alerts'),
        api.get('/admin/momentum').catch(() => null),
      ]);
      setAlerts(alertsRes.data.alerts || []);
      if (momentumRes) setMomentum(momentumRes.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAction = (a) => {
    if (a.action_type === 'open_article_form') {
      onOpenArticle && onOpenArticle();
    } else if (onAction) {
      onAction(a.action_type);
    }
  };

  const severeCount = alerts.filter(a => a.severity === 'high').length;

  return (
    <div className="p-6 rounded-3xl relative overflow-hidden" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.05)', border: '1px solid #EFEAE0' }} data-testid="alerts-panel">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl" style={{ backgroundColor: severeCount > 0 ? '#DC262620' : '#78A18E20' }}>
              <Bell className="w-4 h-4" style={{ color: severeCount > 0 ? '#DC2626' : '#78A18E' }} />
            </div>
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              Your Alerts
              {alerts.length > 0 && (
                <span className="ml-2 text-sm font-normal" style={{ color: '#8C867D' }}>({alerts.length})</span>
              )}
            </h3>
          </div>
          <p className="text-xs mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
            What to post, who to respond to, and what needs a fresh touch — all in one place.
          </p>
        </div>
        <button onClick={() => { setLoading(true); load(); }} title="Refresh" className="p-2 rounded-full" style={{ backgroundColor: '#FDFBF7', border: '1px solid #E6E2D8' }} data-testid="alerts-refresh">
          <RefreshCcw className="w-3.5 h-3.5" style={{ color: '#8C867D' }} />
        </button>
      </div>

      {momentum && (
        <div className="p-4 rounded-2xl mb-4 flex items-center gap-3 flex-wrap" style={{ backgroundColor: `${momentum.tint}12`, border: `1px solid ${momentum.tint}30` }} data-testid="momentum-badge">
          <div className="p-2 rounded-xl flex-shrink-0" style={{ backgroundColor: `${momentum.tint}25` }}>
            <TrendingUp className="w-4 h-4" style={{ color: momentum.tint }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: momentum.tint, letterSpacing: '0.12em' }}>
              This Week's Momentum · {momentum.label}
            </p>
            <p className="text-sm mt-0.5" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>{momentum.message}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {Array.from({ length: momentum.target }).map((_, i) => (
              <span key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: i < momentum.posts_this_week ? momentum.tint : `${momentum.tint}25` }} />
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p className="py-6 text-center" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Checking in on your community…</p>
      ) : alerts.length === 0 ? (
        <div className="py-8 text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: '#78A18E20' }}>
            <span className="text-2xl">🌿</span>
          </div>
          <p style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontWeight: 700, fontSize: 16 }}>You're all caught up</p>
          <p className="mt-1 text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Nothing pressing right now. Rest is holy work too.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((a, idx) => {
            const Icon = ICONS[a.icon] || Bell;
            const st = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info;
            return (
              <div key={idx} className="p-3 rounded-2xl flex items-start gap-3 flex-wrap sm:flex-nowrap" style={{ backgroundColor: st.bg, border: `1px solid ${st.tint}25` }} data-testid={`alert-${a.type}-${idx}`}>
                <div className="p-2 rounded-xl flex-shrink-0" style={{ backgroundColor: `${st.tint}20` }}>
                  <Icon className="w-4 h-4" style={{ color: st.tint }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: st.tint, letterSpacing: '0.1em' }}>{st.label}</p>
                  <p className="text-sm mt-0.5" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>{a.message}</p>
                </div>
                {a.action_label && (
                  <button
                    onClick={() => handleAction(a)}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold transition hover:-translate-y-0.5 flex-shrink-0"
                    style={{ backgroundColor: st.tint, color: '#FDFBF7', fontFamily: 'Nunito, sans-serif' }}
                    data-testid={`alert-action-${a.type}-${idx}`}
                  >
                    {a.action_label}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
